import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { Option } from '@interfaces/common/option';
import { PartnerCompanyResponse } from '@interfaces/partners/responses/partner-company.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { TableActions, TableComponent } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { ModalVincularEmpresaComponent } from './components/modal-vincular-empresa/modal-vincular-empresa.component';
import { MenuItem } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

/**
 * Aba "Empresas" do cadastro de parceiro (Comercial › Parceiros).
 *
 * São duas ações explícitas e separadas, e não um seletor que representa o estado final:
 *
 * - o **seletor** oferece só empresas que o parceiro **não alcança** — nem por deal, nem por
 *   vínculo manual —, e o botão "Vincular" age sobre ele;
 * - a **lista** mostra o escopo inteiro, e o botão "Desvincular" age sobre as linhas
 *   marcadas nela, que só podem ser as que têm vínculo manual.
 *
 * O desenho anterior usava o seletor como estado: o que estivesse nele virava a lista
 * inteira no servidor. Isso apagava vínculo em silêncio — uma empresa que também tivesse
 * deal saía da seleção, e um "salvar" sem nenhuma alteração removia o vínculo manual dela.
 * Enviar só o delta acaba com essa classe de erro e torna as duas ações idempotentes.
 */
@Component({
  selector: 'app-partner-companies',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, ModalVincularEmpresaComponent],
  templateUrl: './partner-companies.component.html',
  styleUrls: ['./partner-companies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerCompaniesComponent implements OnInit {
  /** Parceiro sendo editado. A aba só existe em modo edição. */
  @Input({ required: true }) partnerId!: string;

  readonly permissions = Permissions.Partner;
  readonly empresas = signal<PartnerCompanyResponse[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly podeEditar = signal(false);

  /**
   * Opções do seletor, montadas **pelo servidor**.
   *
   * O critério é "não estar com nenhum parceiro" — nem por vínculo manual, nem por deal —, e
   * inclui os outros parceiros. A tela não teria como calcular isso: ela só conhece o escopo
   * do parceiro que está aberto.
   */
  readonly opcoesDisponiveis = signal<Option[]>([]);

  /** Verdadeiro depois que a consulta de disponíveis respondeu — evita a dica prematura. */
  readonly opcoesCarregadas = signal(false);

  /** Texto da barra flutuante de seleção. */
  readonly mensagemDaSelecao = computed(() => {
    const total = this.marcadasNaLista().length;
    if (total === 0) return '';
    return total === 1 ? '1 empresa selecionada' : `${total} empresas selecionadas`;
  });

  /**
   * Ações da barra flutuante — só desvincular.
   *
   * Vincular fica de fora de propósito: ela age sobre o **seletor**, não sobre a seleção da
   * lista, e juntar as duas na mesma barra sugeriria que o mesmo conjunto alimenta as duas.
   */
  readonly acoesDaSelecao = computed<MenuItem[]>(() => {
    if (this.marcadasNaLista().length === 0) return [];

    return [
      {
        label: 'Desvincular',
        icon: 'pi pi-trash',
        // `delete-btn` é a variante vermelha do `app-floating-menu` (mesma do "Deletar" na
        // listagem de empréstimos). Desvincular tira acesso a dado de empresa: é destrutivo
        // o bastante para não se parecer com as ações neutras da barra.
        styleClass: 'delete-btn',
        disabled: this.saving(),
        command: () => this.desvincular(),
      },
    ];
  });

  /** Ids marcados no seletor, aguardando o clique em "Vincular". */
  /**
   * Empresa escolhida no seletor. **Uma por vez** desde 18/09/2026: o vínculo passou a
   * carregar um valor pretendido próprio, e não existe um valor que sirva para várias
   * empresas de uma vez. O desvínculo continua em lote — ali não há nada por empresa a
   * informar.
   */
  paraVincular: string | null = null;

  /** Linhas marcadas na tabela, aguardando o clique em "Desvincular". */
  readonly marcadasNaLista = signal<PartnerCompanyResponse[]>([]);

  /** A tabela, para conseguir limpar a seleção dela pelo X da barra flutuante. */
  @ViewChild('tabela') private tabela?: TableComponent<PartnerCompanyResponse>;
  @ViewChild('modalVincular') private modalVincular?: ModalVincularEmpresaComponent;

  readonly columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'name', header: 'Empresa' }),
    new ColumnDefinition({ field: 'document', header: 'CNPJ', format: 'cnpj' }),
    new ColumnDefinition({ field: 'origin', header: 'Origem', template: 'origin' }),
  ];

  constructor(
    private readonly partnersService: PartnersHttpService,
    private readonly toast: ToastHelperService,
    private readonly confirmationService: ConfirmationService,
    private readonly permissionsService: PermissionsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.carregar();

    this.permissionsService
      .hasPermission(this.permissions.UPDATE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pode => {
        this.podeEditar.set(pode);
        this.cdr.markForCheck();
      });
  }

  /**
   * Diz se a linha pode ser marcada para desvínculo.
   *
   * Só as que têm vínculo manual. Empresa que chega apenas por deal é derivada: remover o
   * vínculo dela não existe, e deixar o checkbox marcável seria oferecer uma ação que não
   * faz nada — o usuário só descobriria depois de clicar.
   */
  // Toda origem que TEM vínculo manual é desvinculável — inclusive `DealManual`, que é
  // vínculo + deal nascido dele. Esquecer a origem nova aqui deixaria a linha criada pelo
  // fluxo novo sem checkbox: a Legatus não conseguiria desfazer o que ela mesma acabou de
  // fazer. Só `Deal` fica de fora, porque ali não há vínculo a remover.
  readonly podeDesvincular = (empresa: PartnerCompanyResponse): boolean =>
    empresa.origin === 'Manual' ||
    empresa.origin === 'DealAndManual' ||
    empresa.origin === 'DealManual';

  /** Rótulo humano da origem. */
  rotuloDaOrigem(empresa: PartnerCompanyResponse): string {
    switch (empresa.origin) {
      case 'Deal':
        return 'Deal originado';
      case 'Manual':
        return 'Vínculo manual';
      case 'DealManual':
        // Vínculo feito aqui pela Legatus, com o deal nascendo junto. Nome escolhido pelo
        // Breno em 18/09/2026 — é o mesmo rótulo do selo no board.
        return 'Deal manual';
      default:
        return 'Deal + vínculo manual';
    }
  }

  /** Guarda o que a tabela marcou, para a barra flutuante agir sobre isso. */
  onSelecaoDaLista(evento: TableActions<PartnerCompanyResponse>): void {
    this.marcadasNaLista.set(evento?.items ?? []);
    this.cdr.markForCheck();
  }

  /**
   * Limpa a seleção pelo X da barra.
   *
   * Zera dos **dois** lados: o sinal daqui e a seleção interna da tabela. Limpar só um deixaria
   * os checkboxes marcados com a barra fechada — e ela não voltaria, porque daqui a seleção
   * pareceria vazia.
   */
  limparSelecao(): void {
    this.tabela?.clearSelection();
    this.marcadasNaLista.set([]);
    this.cdr.markForCheck();
  }

  /**
   * Abre a modal do vínculo, que é onde o valor do deal é informado.
   *
   * Substituiu a confirmação simples: vincular deixou de ser só gravar um vínculo — ele
   * registra um **Deal manual** junto, e deal sem valor não existe. A modal é a confirmação,
   * e explica o que mais vai acontecer.
   */
  vincular(): void {
    if (!this.paraVincular) return;

    const empresa = this.opcoesDisponiveis().find(o => o.value === this.paraVincular);
    if (!empresa) return;

    this.modalVincular?.abrir(this.partnerId, empresa);
  }

  /** Pede confirmação e desvincula as empresas marcadas na lista. */
  desvincular(): void {
    const ids = this.marcadasNaLista()
      .filter(this.podeDesvincular)
      .map(e => e.companyId);

    if (ids.length === 0) return;

    const quantas = ids.length === 1 ? 'esta empresa' : `estas ${ids.length} empresas`;

    this.confirmationService
      .confirm(
        `Desvincular ${quantas} do parceiro?`,
        'Os usuários do parceiro deixam de enxergar os dados dela. Os deals já registrados continuam como estão — e, se a empresa também vier de um deal, ela permanece no escopo por causa dele.'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmado => {
        if (confirmado) this.persistirDesvinculo(ids);
      });
  }

  /**
   * Recarrega depois que a modal vinculou.
   *
   * Quem grava é a modal, porque é ela que tem o valor do deal. A aba só reage: limpa o
   * seletor e recarrega as duas listas — a empresa sai das disponíveis e entra na lista, e a
   * origem dela passa a refletir o deal recém-criado.
   */
  aoVincular(): void {
    this.paraVincular = null;
    this.carregar();
  }

  /** Remove o vínculo e recarrega. */
  private persistirDesvinculo(companyIds: string[]): void {
    this.saving.set(true);

    this.partnersService
      .removePartnerCompanies(this.partnerId, companyIds)
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Empresas desvinculadas do parceiro.', '');
          // Limpa dos dois lados: as linhas desvinculadas saem da tabela, e uma seleção
          // interna sobrando apontaria para itens que não existem mais na lista.
          this.limparSelecao();
          this.carregar();
        },
        error: erro => this.mostrarErro('Não foi possível desvincular as empresas', erro),
      });
  }

  /**
   * Carrega o escopo atual do parceiro **e** as empresas ainda disponíveis.
   *
   * As duas andam juntas de propósito: vincular tira a empresa do seletor e a põe na lista,
   * desvincular faz o inverso. Recarregar só um lado deixaria a tela mentindo — o seletor
   * ainda oferecendo o que acabou de ser vinculado, ou escondendo o que acabou de sair.
   */
  private carregar(): void {
    this.carregarOpcoes();
    this.loading.set(true);

    this.partnersService
      .getPartnerCompanies(this.partnerId)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: lista => {
          this.empresas.set(lista ?? []);
          this.marcadasNaLista.set([]);
          this.cdr.markForCheck();
        },
        error: erro => this.mostrarErro('Não foi possível carregar as empresas do parceiro', erro),
      });
  }

  /** Empresas ainda livres para vínculo, recortadas pelo servidor. */
  private carregarOpcoes(): void {
    this.partnersService
      .getAvailableCompanies(this.partnerId)
      .pipe(
        map(lista => (lista ?? []).map(c => ({ label: c.name, value: c.companyId }))),
        catchError(() => of([] as Option[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(opts => {
        this.opcoesDisponiveis.set(opts);
        this.opcoesCarregadas.set(true);
        this.cdr.markForCheck();
      });
  }

  /**
   * Mostra a mensagem que o backend mandou, e só cai no texto genérico quando não há uma.
   */
  private mostrarErro(titulo: string, erro: { errors?: { message: string }[] }): void {
    this.toast.showToastError(titulo, erro?.errors?.length ? erro.errors[0].message : '');
  }
}
